/**
 * How the households are doing, and who leaves because of it.
 *
 * `DESIGN-GOVERNORS.md` §10 asks for one thing and forbids three:
 *
 * > Families already move out after three negative cycles. Extended: each family
 * > periodically evaluates a **QoL sum over measured quantities only** (Law 1):
 * > cash trend, employment, actual commute minutes, noise-events heard at home,
 * > brownout-hours, war-damage proximity, green cells within radius 3. Persistent
 * > low QoL → migrate […] Population is thus the one poll that can't be spun — a
 * > governor's true approval rating is their net migration, and it is public in
 * > the Chronicle.
 *
 * The forbidden three are what makes it a *poll*: no opinion, no mood, no
 * approval. Nothing in this file asks a family how it feels. Every term is a
 * quantity some other part of the district counted for its own reasons —
 * `PowerManager`'s shed set, `WaterManager`'s restriction, `LabourManager`'s
 * roster, the treasury's cash, the ground's own state — and this class does
 * arithmetic on them and nothing else.
 *
 * ## The one property everything else here is arranged around
 *
 * > **In a district with power and water for everybody, a household is strained
 * > if and only if its cash is negative.**
 *
 * That is the rule Parkington has run since long before there was a polity —
 * `EconomyManager.checkFamilyHardship`, three consecutive cycles under zero and
 * the family is gone — and it is recovered here exactly, not approximately. The
 * weights below are chosen so that insolvency alone always crosses the line and
 * everything a district does to itself in the ordinary course (a paved-over
 * meadow, a job, a shortage that never happened) never can. `tests/welfare.test.ts`
 * asserts both directions, and the eleven golden masters assert it a second way:
 * no district anybody has ever recorded here has had a dark home or a dry tap,
 * so none of them may move.
 *
 * ## The exposure is the household's, not the building's
 *
 * `PowerManager` counts dark cycles per *house* and `WaterManager` counts dry
 * cycles per *building*, which is right for a ledger of the grid and wrong for a
 * measure of what happened to a family: a house that spent forty cycles in the
 * dark and then had new people move in would drive them out on arrival, and the
 * one after them, and the one after that. So the exposure counted here starts
 * when a family is first seen and dies with them.
 *
 * That is the only state this class holds, and it holds none of it in a district
 * where nothing has gone wrong: an entry appears the first cycle a household is
 * dark, dry, or below the line, and `canonicalState()` is `undefined` until one
 * does. A world in which this mechanic has never happened is indistinguishable
 * from a world that does not have it (ADR-017 D105).
 *
 * ## What this class cannot see
 *
 * A hash type. Not the houses, not the ground, not the traffic and not — the
 * point of the arrangement — anything under the soil. It is handed a bag of
 * households once per settled budget cycle and hands back a verdict; `SurfaceSim`
 * is the one object that knows a household is the Harrisons and that a departure
 * means a vacant house. `tests/purity.test.ts` pins the import list, and it pins
 * it harder here than anywhere else: a quality-of-life score over a district is
 * one line away from being an ecology figure, this file legitimately counts
 * *green cells*, and everything it counts ends up in the Chronicle.
 *
 * The green term is therefore worth being explicit about. It counts unsealed
 * ground within a radius of a home, which the record has published as
 * `land.natural` since ADR-012 and which is a fact about the mayor's concrete.
 * It is not a bush count, not a food figure, not a portal count, and not
 * anything a colony would recognise as being about it.
 *
 * See `docs/quality-of-life.md`.
 */

import { Canonical, hashString } from '../hashState';

/**
 * Why a household left, in the order a tie is broken.
 *
 * The cause is the term that subtracted most from the score at the moment the
 * family gave up — not a story, and not a judgement. Ordered so that iteration
 * over it is a named order (`docs/determinism.md` rule 4) and so that two terms
 * of equal weight resolve the same way in every runtime.
 */
export const DEPARTURE_CAUSE_ORDER = Object.freeze([
  /** Cash below zero. The rule that predates every other line in this file. */
  'insolvency',
  /** Cycles this household's home spent shed by the grid. */
  'brownout',
  /** Cycles its taps spent shut off by the mains. */
  'dryTaps',
  /** Sealed ground where the green cells used to be. */
  'sealedGround',
  /** The state of the roads every trip out of the district is made on. */
  'badRoads',
] as const);

export type DepartureCause = (typeof DEPARTURE_CAUSE_ORDER)[number];

/**
 * What a household's life is worth, in weights.
 *
 * Every number here is a choice and none of them has been playtested. What each
 * one *was chosen against* is written beside it, which is the standard ADR-015
 * D87 set for a facility's draw and the only standard available while there is
 * nobody to calibrate against.
 */
export interface WelfareConfig {
  /**
   * The score a household with nothing measurable wrong with it has.
   *
   * A ceiling rather than a baseline: every term below subtracts, except
   * employment, and the sum is clamped here. A district cannot make anybody
   * *happier* than untroubled, which is a modelling choice and also the honest
   * limit of a sum over shortages.
   */
  QOL_INTACT: number;

  /** Below this, a household is *strained* and its patience starts draining. */
  MIGRATE_BELOW: number;

  /**
   * Cycles of patience a household has at its most impatient.
   *
   * Three, because three is what `checkFamilyHardship` counted since before
   * there was a design document — and this is the floor of the range rather
   * than the whole of it, so that **no household ever leaves sooner than the old
   * rule would have taken them.** See `PATIENCE_SPREAD` and `toleranceOf`.
   */
  MIGRATE_AFTER: number;

  /**
   * How much more patience the most patient household has than the least.
   *
   * The fix for the cliff. Every household in a shortage suffers identically, so
   * a single threshold takes the whole town in one cycle — measured at eighteen
   * families of twenty-two, twenty-one of twenty-six, twenty-three of thirty.
   * Spreading the tolerance turns that into a bleed without making it a lottery:
   * a household's tolerance is a pure function of its family id (`toleranceOf`),
   * so it is fixed the moment they move in, reproducible, and holds no state.
   */
  PATIENCE_SPREAD: number;

  /**
   * How fast patience drains, per unit of score below the line, per cycle.
   *
   * Two, which is what makes an insolvent household — score clamped to zero,
   * half a unit below the line — spend patience at exactly one cycle per cycle.
   * That is the old rule's rate, and it is why `MIGRATE_AFTER` cycles of
   * insolvency still costs `MIGRATE_AFTER` of patience.
   *
   * Everything gentler than destitution drains proportionally slower. A home
   * fully in the dark scores 0.40 and drains at a fifth of the rate, which is
   * this whole mechanic in one number: **a blackout is not an eviction notice,
   * it is a reason to start looking.**
   */
  DRAIN_RATE: number;

  /**
   * Cycles of exposure a household forgets for each cycle the shortage is over.
   *
   * Without this the ledger is a death sentence with a delay. Exposure was
   * monotone when ADR-018 shipped it and that was survivable, because what
   * decided a departure was a *streak* and one good cycle reset it. A reservoir
   * has no reset: a household that banked forty dark cycles scores 0.40 forever,
   * stays under the line forever, and drains to empty however long the lights
   * have been back on. Measured on a district whose power was restored after
   * forty-five cycles — it went on losing families until there were none, an hour
   * of sim-time after the last dark cycle.
   *
   * So a shortage is forgotten at half the rate it accumulated: two lit cycles
   * per dark one. Slower than the harm, like the patience it feeds, and not
   * instant, because a district should not be able to undo a season of blackouts
   * by keeping the lights on for one cycle.
   *
   * `darkHouseholdCycles` in the ledger is untouched by this and stays monotone.
   * What a district *did* to its households is not forgotten; what a household is
   * still carrying is.
   */
  EXPOSURE_FORGIVEN: number;

  /**
   * How fast patience comes back, per unit of score above the line, per cycle.
   *
   * Below the drain rate on purpose. The old rule forgave a household the
   * instant its cash went positive — three bad cycles and one good one left no
   * trace — and a district that could undo a season of blackouts with one lit
   * cycle is a district where the mayor has nothing to lose. Recovery is real
   * and it is slower than the damage.
   */
  RECOVER_RATE: number;

  /**
   * What negative cash costs.
   *
   * The whole score, so that an insolvent household is strained however green
   * its street and however good its job. That is not a weighting judgement, it is
   * the old rule stated as a weight: `cash < 0` was the entire test, and it has
   * to remain sufficient or every hash in this repository moves.
   */
  INSOLVENCY: number;

  /**
   * What a home fully in the dark costs, and how many cycles "fully" is.
   *
   * §12 prints the worked example: *"The Harrisons left: 41 brownout-hours"*. At
   * these two numbers a household with 41 dark cycles is past the tolerance and
   * scores 0.40 — below the line, with room to spare — so the design's own
   * sentence is what the calibration was fitted to, and the threshold it implies
   * (34 cycles in the dark, alone, with nothing else wrong) is a consequence
   * rather than a second choice.
   *
   * Past the tolerance the term is at its floor, so 41 cycles and 400 score the
   * same. There is nothing worse than a house with no power.
   */
  BROWNOUT: number;
  BROWNOUT_TOLERANCE: number;

  /**
   * The same, for taps.
   *
   * Equal to the grid's on purpose. ADR-014 D77 argued that a dry home and a dark
   * home are the same shape of fact to a district and should be measured the same
   * way; this is the first place where anything reads either ledger, and giving
   * them different weights would be the first time this build claimed to know
   * which shortage is worse.
   */
  DRY: number;
  DRY_TOLERANCE: number;

  /**
   * What ground fully sealed within `GREEN_RADIUS` of a home costs.
   *
   * Deliberately below `MIGRATE_BELOW`'s distance from intact, which means the
   * meadow can never drive anybody out on its own. A mayor who paves the whole
   * district makes every household measurably worse off and loses nobody for it
   * — which is a statement about this game rather than about the number, and it
   * is the statement §10 makes when it lists green cells beside brownout-hours
   * and does not say they are comparable.
   */
  GREEN: number;

  /** Cells out from a home the green count looks, in the Chebyshev sense. §10. */
  GREEN_RADIUS: number;

  /**
   * What roads at zero quality cost a household.
   *
   * The one term on this list the mayor sets directly. Road quality is the
   * infrastructure budget's output and has been in the game since long before
   * there was a polity; until now it decided how fast cars moved and nothing
   * else. §10 lists *commute minutes*, this build cannot measure a household's
   * own commute (`docs/quality-of-life.md`), and the quality of the roads it
   * would be spent on is the part of that sentence the district already counts.
   *
   * Sized like the meadow and for the same reason: `GREEN + ROADS` is below the
   * distance from intact to the line, so neither concrete nor potholes nor both
   * together can take a household from a mayor who keeps them solvent, lit and
   * watered. What they do is make the score *move* in ordinary play, which it
   * did not before — a district's roads climb from 50 to 100 over its first
   * couple of sim-minutes, and its households feel it.
   */
  ROADS: number;

  /**
   * The district mean score at which newcomers stop arriving with a full purse.
   *
   * Above this a household moving in is the household that has always moved in.
   * Below it they arrive with proportionally less, down to `NEWCOMER_FLOOR` —
   * which is not a penalty the game is imposing on the mayor but the oldest
   * mechanism in housing: a place that is coming apart gets cheap, and cheap is
   * who it gets. The district's population can hold perfectly steady while its
   * tax base goes.
   *
   * Set well below any ordinary district — they measure 0.90 to 0.92 — so that
   * this is a fact about a district in trouble and not a slope everybody is on.
   */
  NEWCOMER_PLATEAU: number;

  /** The mean score at or below which newcomers arrive with the least. */
  NEWCOMER_FLOOR_QOL: number;

  /** The least a newcomer can arrive with, as a fraction of the standard purse. */
  NEWCOMER_FLOOR: number;

  /**
   * What a federal job is worth to a household, beyond its wage.
   *
   * Small, and the wage is doing the real work: a job pays cash and cash is the
   * dominant term. This is the residue §10 means by listing *employment*
   * separately from *cash trend* — having somewhere to be, distinct from what it
   * pays. It cannot save an insolvent household and is not meant to.
   */
  EMPLOYMENT: number;
}

export const DEFAULT_WELFARE_CONFIG: WelfareConfig = {
  QOL_INTACT: 1,
  MIGRATE_BELOW: 0.5,
  MIGRATE_AFTER: 3,
  PATIENCE_SPREAD: 4,
  DRAIN_RATE: 2,
  RECOVER_RATE: 0.5,
  EXPOSURE_FORGIVEN: 0.5,
  INSOLVENCY: 1,
  BROWNOUT: 0.6,
  BROWNOUT_TOLERANCE: 40,
  DRY: 0.6,
  DRY_TOLERANCE: 40,
  GREEN: 0.2,
  GREEN_RADIUS: 3,
  ROADS: 0.15,
  EMPLOYMENT: 0.1,
  NEWCOMER_PLATEAU: 0.75,
  NEWCOMER_FLOOR_QOL: 0.35,
  NEWCOMER_FLOOR: 0.25,
};

/** One household, as the measure is allowed to see it. */
export interface HouseholdReading {
  familyId: number;
  /** Cash at the close of the cycle, after the wage and after the bills. */
  cash: number;
  /** Whether they hold a federal post. Not whether they reached it. */
  employed: boolean;
  /**
   * Their home is shed by the grid this cycle, *and* it is a home.
   *
   * A construction site the grid stalled is not a family in the dark, so the
   * caller resolves that distinction before it gets here — this class cannot see
   * a house.
   */
  dark: boolean;
  /** The mains has shut their taps this cycle. */
  dry: boolean;
  /** Unsealed cells within the green radius of home. */
  green: number;
  /** Cells looked at, which is the window clipped at the map's edge. */
  greenOf: number;
  /**
   * Patience already spent, in cycles. Carried in and handed back out.
   *
   * The reservoir lives on the family — `HouseManager`'s `hardshipCycles`, which
   * has counted a household's distance from giving up since before there was a
   * design document and still does. It travels in the reading rather than in a
   * map here for the reason the whole class is arranged around: this object
   * cannot see a family, and a second copy of a number that is already in the
   * hash is a second thing that can be wrong.
   */
  spent: number;
}

/** The district's households, in family id order — the order they moved in. */
export interface WelfareReading {
  households: HouseholdReading[];
  /**
   * The district's roads, 0–1. One number, shared by everybody.
   *
   * `EconomyManager.getRoadQuality()` over a hundred. Not per household, because
   * the road network is not per household — the infrastructure budget buys one
   * quality for the whole district and every commute in it is over the result.
   */
  roads: number;
}

/** One household leaving, and the measurement that named the reason. */
export interface Departure {
  familyId: number;
  cause: DepartureCause;
  /**
   * The number behind the cause, in the cause's own unit.
   *
   * Cycles for the two shortages, sealed cells for the ground, and the household's
   * cash for insolvency. The panel quotes it and the Chronicle counts the cause;
   * nothing anywhere interprets it.
   */
  measure: number;
}

/** What one settled cycle did to the district's households. */
export interface WelfareVerdict {
  /** Every household's patience spent after this cycle, in family id order. */
  spent: Array<[number, number]>;
  /** Who is leaving, ascending by family id. Empty on almost every cycle. */
  departures: Departure[];
}

/** One household's accumulated exposure. Absent while there is none. */
interface Exposure {
  /** Cycles this *family* has spent in the dark. Not the house's ledger. */
  dark: number;
  /** Cycles this family has spent without water. */
  dry: number;
}

/** The measure's complete state. See `WelfareManager.save()`. */
export interface WelfareSnapshot {
  /** `[familyId, dark, dry]`, ascending by family id. */
  exposure: Array<[number, number, number]>;
  /** `[familyId, score]` at the last settled cycle. A readout, not a decision. */
  qol: Array<[number, number]>;
  strainedCycles: number;
  darkHouseholdCycles: number;
  dryHouseholdCycles: number;
  departures: number;
  arrivalsRefused: number;
  strainedNow: number;
  /** `[cause, count]` in `DEPARTURE_CAUSE_ORDER`, non-zero entries only. */
  departuresBy: Array<[DepartureCause, number]>;
}

/** The district's households, as a panel or a season record wants them. */
export interface WelfareLedger {
  /** Household-cycles spent below the line. A sum. */
  strainedCycles: number;
  /** Household-cycles a family spent in the dark. A sum. */
  darkHouseholdCycles: number;
  /** Household-cycles a family spent without water. A sum. */
  dryHouseholdCycles: number;
  /** Families that have left, lifetime. A sum. */
  departures: number;
  /**
   * Cycles a vacant home stood empty because nowhere habitable was on offer.
   *
   * The arrival gate's ledger, and zero in every district where the lights have
   * always been on. It is the number that says a district's population held
   * steady *because nobody would come*, rather than because nobody left.
   */
  arrivalsRefused: number;
  /** Departures by the cause that named them, non-zero causes only. */
  departuresBy: Partial<Record<DepartureCause, number>>;
}

/**
 * What the housing market is willing to do about a vacant home.
 *
 * A frozen adapter `SurfaceSim` builds, on the same terms as the labour market's
 * `CommuteSource`: `HouseManager` is asked this on every fill and must not be
 * able to reach a flow, a score or a ledger through it.
 */
export interface HousingMarket {
  /** Whether anybody would move into this home right now. */
  admits(houseId: number): boolean;
  /** What they arrive with, as a fraction of the standard purse. */
  newcomerScale(): number;
  /** Called when a home was on offer and nothing habitable was among them. */
  refused(): void;
}

/**
 * The district's households, measured.
 *
 * Owned by `SurfaceSim`, settled last of everything in a metered budget cycle —
 * after the treasury, both meters, the market and the payroll — because a
 * household should be asked how it is doing once everything that could be done to
 * it this cycle has been. Holds no `Rng`: whether a family gives up is arithmetic,
 * and which family it happens to is decided by what happened to them.
 */
export class WelfareManager {
  private config: WelfareConfig;

  /**
   * Households with something the matter. Empty in a district with nothing.
   *
   * Insertion is by ascending family id when it happens at all, and every walk
   * sorts anyway (`docs/determinism.md` rule 4) — a `Map`'s order is its
   * insertion order, and a family can first appear here in any cycle.
   */
  private readonly exposure = new Map<number, Exposure>();

  /**
   * Every household's score at the last settled cycle.
   *
   * A *reading*, and deliberately not in `canonicalState()`. The score is a pure
   * function of quantities that are all hashed somewhere else — cash, the roster,
   * the ground, and the exposure above — recomputed from scratch every cycle, so
   * hashing it would restate its own inputs. Two districts that agree on every
   * streak and every departure and disagree here have not diverged; they will
   * produce the same streaks next cycle, because nothing reads this but a panel.
   *
   * It is in `save()`, because a restored district whose panel went blank for a
   * cycle would be a district somebody noticed reloading.
   */
  private readonly qol = new Map<number, number>();

  private strainedCycles = 0;
  private darkHouseholdCycles = 0;
  private dryHouseholdCycles = 0;
  private departureCount = 0;
  private arrivalsRefused = 0;
  /** Households under the line at the last settled cycle. A level, recomputed. */
  private strainedNow = 0;
  private readonly departuresBy = new Map<DepartureCause, number>();

  constructor(config: Partial<WelfareConfig> = {}) {
    this.config = { ...DEFAULT_WELFARE_CONFIG, ...config };
  }

  getConfig(): WelfareConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<WelfareConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /** Whether anything has ever gone wrong here. See `canonicalState()`. */
  get reported(): boolean {
    return this.exposure.size > 0 || this.departureCount > 0 ||
           this.strainedCycles > 0 || this.darkHouseholdCycles > 0 ||
           this.dryHouseholdCycles > 0 || this.arrivalsRefused > 0;
  }

  /** One household's score at the last settled cycle, or `null` for unmeasured. */
  qolOf(familyId: number): number | null {
    const score = this.qol.get(familyId);
    return score === undefined ? null : score;
  }

  /** The district's mean score, or `null` while nobody lives here. */
  meanQol(): number | null {
    if (this.qol.size === 0) return null;
    let sum = 0;
    for (const familyId of this.measuredFamilies()) sum += this.qol.get(familyId)!;
    return sum / this.qol.size;
  }

  /** Households below the line at the last settled cycle. A level. */
  strainedCount(): number {
    return this.strainedNow;
  }

  /** One household's exposure, for a panel and for a departure's measure. */
  exposureOf(familyId: number): { dark: number; dry: number } {
    const held = this.exposure.get(familyId);
    return held ? { ...held } : { dark: 0, dry: 0 };
  }

  /**
   * How much patience this household has, in cycles, before it gives up.
   *
   * A pure function of the family id, and the only source of difference between
   * two households in identical circumstances. `hashString` is the same mixer
   * the state hashes use, so it agrees across runtimes for the same reason every
   * other number here does — and because it is a *function* rather than a draw,
   * it costs no state, consumes no generator, and can be asked again a thousand
   * seasons later with the same answer.
   *
   * The range starts at `MIGRATE_AFTER` and only goes up. That is the property
   * that lets the old rule survive a spread: **no household leaves sooner than
   * three cycles of insolvency**, which is what it has always taken, and some
   * take longer. A shortage that used to empty a town in one cycle now empties
   * it over `PATIENCE_SPREAD` of them.
   */
  toleranceOf(familyId: number): number {
    // Sixteen hex digits in, one fraction out. The low bits are used because
    // `hashString` mixes into all of them and a leading digit would bucket
    // consecutive ids together — families move in consecutively.
    const digits = hashString(`household:${familyId}`).slice(-8);
    const fraction = parseInt(digits, 16) / 0x1_0000_0000;
    return this.config.MIGRATE_AFTER + fraction * this.config.PATIENCE_SPREAD;
  }

  /**
   * What a household moving in right now arrives with, as a fraction of the
   * standard purse.
   *
   * One above the plateau, and falling to `NEWCOMER_FLOOR` as the district's mean
   * score goes. Not a penalty on the mayor: it is what a cheap house is worth to
   * whoever can afford one, and it is how a district's population can hold
   * perfectly steady while its tax base leaves. `null` — meaning *the standard
   * purse* — until the first cycle has settled and there is a mean to read.
   */
  newcomerScale(): number {
    const mean = this.meanQol();
    if (mean === null) return 1;
    const { NEWCOMER_PLATEAU, NEWCOMER_FLOOR_QOL, NEWCOMER_FLOOR } = this.config;
    if (mean >= NEWCOMER_PLATEAU) return 1;
    const span = NEWCOMER_PLATEAU - NEWCOMER_FLOOR_QOL;
    if (span <= 0) return NEWCOMER_FLOOR;
    const t = Math.min(1, Math.max(0, (mean - NEWCOMER_FLOOR_QOL) / span));
    return NEWCOMER_FLOOR + t * (1 - NEWCOMER_FLOOR);
  }

  /**
   * Record a cycle in which a vacant home was on offer and nobody would take it.
   *
   * Called by `SurfaceSim`, which owns the gate, for the reason every other
   * ledger entry here is written by somebody else: this class cannot see a house
   * and has no way to know one stood empty.
   */
  recordRefusedArrival(): void {
    this.arrivalsRefused++;
  }

  // --- The cycle ------------------------------------------------------------

  /**
   * Score every household, advance the streaks, and name who is leaving.
   *
   * Pure in everything but this class's own counters: it removes nobody, evicts
   * nobody and writes no news. `SurfaceSim` applies the verdict, because a
   * departure is a house going vacant and a shareholding being unwound and a line
   * in a ticker, and none of those is a thing a measure should be able to reach.
   */
  settle(reading: WelfareReading): WelfareVerdict {
    const spent: Array<[number, number]> = [];
    const departures: Departure[] = [];
    const { MIGRATE_BELOW, DRAIN_RATE, RECOVER_RATE } = this.config;
    let strained = 0;

    // Households arrive in family id order and every walk below is over this
    // array, so nothing here depends on a `Map`'s iteration order.
    for (const household of reading.households) {
      const held = this.exposure.get(household.familyId);
      let dark = held?.dark ?? 0;
      let dry = held?.dry ?? 0;

      // The ledger's totals below are monotone and this household's own burden
      // is not: what a district did is remembered, what a family is still
      // carrying fades. See `EXPOSURE_FORGIVEN`.
      if (household.dark) {
        dark++;
        this.darkHouseholdCycles++;
      } else {
        dark = Math.max(0, dark - this.config.EXPOSURE_FORGIVEN);
      }
      if (household.dry) {
        dry++;
        this.dryHouseholdCycles++;
      } else {
        dry = Math.max(0, dry - this.config.EXPOSURE_FORGIVEN);
      }

      const score = this.score(household, dark, dry, reading.roads);
      this.qol.set(household.familyId, score);

      // Patience moves by how far the household is from the line, not by whether
      // it is over it. A district that is slightly bad bleeds slowly and one
      // that is destitute empties fast, and no cycle is worth the same as any
      // other — which is the whole of the difference between this and a streak.
      const gap = score - MIGRATE_BELOW;
      const tolerance = this.toleranceOf(household.familyId);
      let patience = household.spent;
      if (gap < 0) {
        patience += -gap * DRAIN_RATE;
        this.strainedCycles++;
        strained++;
      } else {
        patience = Math.max(0, patience - gap * RECOVER_RATE);
      }

      if (dark > 0 || dry > 0) {
        this.exposure.set(household.familyId, { dark, dry });
      } else if (held !== undefined) {
        // Recovered, and with nothing left to remember. An entry that stayed at
        // two zeroes would be a district that could not go back to being one
        // where this never happened.
        this.exposure.delete(household.familyId);
      }

      spent.push([household.familyId, patience]);

      if (patience >= tolerance) {
        departures.push(this.departure(household, dark, dry, reading.roads));
      }
    }

    this.strainedNow = strained;
    this.prune(reading);
    return { spent, departures };
  }

  /**
   * Forget a household that has left.
   *
   * Called by `SurfaceSim` once it has actually removed them, rather than done
   * inside `settle`, so that a caller which ignores the verdict — a test, a
   * prediction, a client running ahead — does not silently lose the exposure of
   * families who are still living here.
   */
  release(familyId: number): void {
    this.exposure.delete(familyId);
    this.qol.delete(familyId);
  }

  /**
   * Record a departure against its cause.
   *
   * Separate from `settle` for the reason `LabourManager.recordPayroll` is
   * separate from `payroll()`: the verdict is a proposal until somebody acts on
   * it, and a ledger that counted proposals would be counting families who are
   * still here.
   */
  recordDeparture(cause: DepartureCause): void {
    this.departureCount++;
    this.departuresBy.set(cause, (this.departuresBy.get(cause) ?? 0) + 1);
  }

  /**
   * A household's quality of life, in `[0, QOL_INTACT]`.
   *
   * Every term is a subtraction from intact except employment, and the sum is
   * clamped at both ends. The clamp at the bottom is what makes insolvency
   * *dominant* rather than merely heavy: a household under zero is at the floor
   * whatever else is true of it, so the old rule cannot be outvoted.
   */
  private score(
    household: HouseholdReading,
    dark: number,
    dry: number,
    roads: number
  ): number {
    const c = this.config;
    let score = c.QOL_INTACT;
    if (household.cash < 0) score -= c.INSOLVENCY;
    score -= c.BROWNOUT * Math.min(1, dark / Math.max(1, c.BROWNOUT_TOLERANCE));
    score -= c.DRY * Math.min(1, dry / Math.max(1, c.DRY_TOLERANCE));
    score -= c.GREEN * this.sealedFraction(household);
    score -= c.ROADS * (1 - Math.min(1, Math.max(0, roads)));
    if (household.employed) score += c.EMPLOYMENT;
    return Math.min(c.QOL_INTACT, Math.max(0, score));
  }

  /** Ground within the radius that is not meadow, in `[0, 1]`. */
  private sealedFraction(household: HouseholdReading): number {
    if (household.greenOf <= 0) return 0;
    const green = Math.min(household.green, household.greenOf);
    return 1 - green / household.greenOf;
  }

  /**
   * Which term took the most off this household's score.
   *
   * Walked in `DEPARTURE_CAUSE_ORDER` with a strict comparison, so a tie goes to
   * the earlier cause — which puts insolvency first, where the old rule was.
   */
  private departure(
    household: HouseholdReading,
    dark: number,
    dry: number,
    roads: number
  ): Departure {
    const c = this.config;
    const weights: Record<DepartureCause, number> = {
      insolvency: household.cash < 0 ? c.INSOLVENCY : 0,
      brownout: c.BROWNOUT * Math.min(1, dark / Math.max(1, c.BROWNOUT_TOLERANCE)),
      dryTaps: c.DRY * Math.min(1, dry / Math.max(1, c.DRY_TOLERANCE)),
      sealedGround: c.GREEN * this.sealedFraction(household),
      badRoads: c.ROADS * (1 - Math.min(1, Math.max(0, roads))),
    };
    const measures: Record<DepartureCause, number> = {
      insolvency: household.cash,
      brownout: dark,
      dryTaps: dry,
      sealedGround: household.greenOf - Math.min(household.green, household.greenOf),
      badRoads: Math.round(Math.min(1, Math.max(0, roads)) * 100),
    };

    let cause: DepartureCause = DEPARTURE_CAUSE_ORDER[0];
    for (const candidate of DEPARTURE_CAUSE_ORDER) {
      if (weights[candidate] > weights[cause]) cause = candidate;
    }
    return { familyId: household.familyId, cause, measure: measures[cause] };
  }

  /**
   * Drop exposure and scores for families the reading no longer contains.
   *
   * The move `PowerManager.pruneDarkCycles` makes, for the same reason: a
   * household that burned through forty dark cycles and then left has nobody to
   * be measured, and an entry nothing can ever clear is a leak that a settlement
   * worker running a thousand seasons would eventually notice.
   */
  private prune(reading: WelfareReading): void {
    if (this.exposure.size === 0 && this.qol.size === 0) return;
    const present = new Set<number>();
    for (const household of reading.households) present.add(household.familyId);
    for (const familyId of this.exposedFamilies()) {
      if (!present.has(familyId)) this.exposure.delete(familyId);
    }
    for (const familyId of this.measuredFamilies()) {
      if (!present.has(familyId)) this.qol.delete(familyId);
    }
  }

  // --- State ----------------------------------------------------------------

  /** Family ids with exposure, ascending. See `docs/determinism.md` rule 4. */
  private exposedFamilies(): number[] {
    return Array.from(this.exposure.keys()).sort((a, b) => a - b);
  }

  /** Family ids with a score, ascending. Same rule, same reason. */
  private measuredFamilies(): number[] {
    return Array.from(this.qol.keys()).sort((a, b) => a - b);
  }

  /** Causes that have named at least one departure, in the design's order. */
  private recordedCauses(): DepartureCause[] {
    return DEPARTURE_CAUSE_ORDER.filter((cause) => (this.departuresBy.get(cause) ?? 0) > 0);
  }

  /** The lifetime figures. Monotone sums, so a season's is a subtraction. */
  getLedger(): WelfareLedger {
    const departuresBy: Partial<Record<DepartureCause, number>> = {};
    for (const cause of this.recordedCauses()) departuresBy[cause] = this.departuresBy.get(cause)!;
    return {
      strainedCycles: this.strainedCycles,
      darkHouseholdCycles: this.darkHouseholdCycles,
      dryHouseholdCycles: this.dryHouseholdCycles,
      departures: this.departureCount,
      arrivalsRefused: this.arrivalsRefused,
      departuresBy,
    };
  }

  save(): WelfareSnapshot {
    return {
      exposure: this.exposedFamilies().map((id) => {
        const held = this.exposure.get(id)!;
        return [id, held.dark, held.dry] as [number, number, number];
      }),
      qol: this.measuredFamilies().map((id) => [id, this.qol.get(id)!] as [number, number]),
      strainedCycles: this.strainedCycles,
      darkHouseholdCycles: this.darkHouseholdCycles,
      dryHouseholdCycles: this.dryHouseholdCycles,
      departures: this.departureCount,
      arrivalsRefused: this.arrivalsRefused,
      strainedNow: this.strainedNow,
      departuresBy: this.recordedCauses().map(
        (cause) => [cause, this.departuresBy.get(cause)!] as [DepartureCause, number]
      ),
    };
  }

  /**
   * Restore from `save()`.
   *
   * Including the exposure, which is this class's rainfall: a district restored
   * with everybody's dark cycles forgotten is a district whose brownout ended
   * because somebody reloaded, and the families it would then go on keeping are
   * families the season record already said were leaving.
   */
  load(state: WelfareSnapshot): void {
    this.exposure.clear();
    for (const [familyId, dark, dry] of state.exposure) {
      this.exposure.set(familyId, { dark, dry });
    }
    this.qol.clear();
    for (const [familyId, score] of state.qol) this.qol.set(familyId, score);
    this.strainedCycles = state.strainedCycles;
    this.darkHouseholdCycles = state.darkHouseholdCycles;
    this.dryHouseholdCycles = state.dryHouseholdCycles;
    this.departureCount = state.departures;
    this.arrivalsRefused = state.arrivalsRefused;
    this.strainedNow = state.strainedNow;
    this.departuresBy.clear();
    for (const [cause, count] of state.departuresBy) this.departuresBy.set(cause, count);
  }

  /**
   * Order-stable view of the households, for hashing — and nothing while every
   * one of them is fine.
   *
   * The move the flows, the estate, the market and the Chronicle all make, and
   * the one this class needed most: every district in this repository's golden
   * masters has full power, full water and nobody under zero, so none of them has
   * an entry here and none of their hashes may move. A measure that wrote a bag of
   * zeroes for every family would have re-recorded eleven runs to say that nothing
   * happened.
   *
   * The scores are not in it. See `qol` above: they restate hashed inputs.
   */
  canonicalState(): Canonical | undefined {
    if (!this.reported) return undefined;
    return {
      exposure: this.exposedFamilies().map((id) => {
        const held = this.exposure.get(id)!;
        return `${id}:${held.dark}:${held.dry}`;
      }),
      strainedCycles: this.strainedCycles,
      darkHouseholdCycles: this.darkHouseholdCycles,
      dryHouseholdCycles: this.dryHouseholdCycles,
      departures: this.departureCount,
      arrivalsRefused: this.arrivalsRefused,
      departuresBy: this.recordedCauses().map(
        (cause) => `${cause}:${this.departuresBy.get(cause)}`
      ),
    };
  }
}
